import { z } from 'zod'
import {
  plannerDetailSchema,
  plannerPublishedEventSchema,
  plannerEditSchema,
  plannerEditResultSchema,
  plannerRestoreSchema,
  plannerSelectionRequestSchema,
  plannerHotelCandidatesSchema,
  plannerHotelPageDetailSchema,
  plannerHotelRoomCandidatesSchema,
  plannerFlightCandidatesSchema,
  plannerFlightPageDetailSchema,
  plannerOperationSchema,
  plannerSummarySchema,
  type PlannerEdit,
  type PlannerRestore,
  type PlannerSelectionRequest,
} from '@cione/contracts'
import type { HttpClient } from './http'

export const plannerListSchema = z.object({
  items: z.array(
    z.object({
      planner_id: z.string().min(1),
      version_id: z.string().min(1),
      version_no: z.number().int().positive(),
      revision: z.number().int().nonnegative(),
      updated_at: z.iso.datetime(),
      summary: plannerSummarySchema,
    }),
  ),
  next_cursor: z.string().min(1).nullable(),
})
export type PlannerList = z.infer<typeof plannerListSchema>

const historySchema = z.object({
  items: z.array(
    z.object({
      version_id: z.string(),
      version_no: z.number().int(),
      revision: z.number().int(),
      title: z.string(),
      creation_kind: z.string(),
      is_latest: z.boolean(),
    }),
  ),
  next_before_version_no: z.number().int().nullable(),
})
export type PlannerHistory = z.infer<typeof historySchema>
export interface PlannerHotelFilter {
  page?: number
  query_text?: string
  sort?: 'default' | 'price_asc' | 'price_desc' | 'score_desc' | 'distance_asc'
  min_price_minor?: number
  max_price_minor?: number
  currency?: string
}
const part = encodeURIComponent
const versionPath = (version: string) => `/api/planner/versions/${part(version)}`

/** Ordinary authenticated HTTP; no TripContext, ordering or client-authored quote facts. */
export function createPlannerApi(http: HttpClient) {
  return {
    conversation(plan: string, signal?: AbortSignal) {
      return http.request({
        method: 'GET',
        path: `/api/planner/plans/${part(plan)}/conversation`,
        schema: z.object({ planner_id: z.string().min(1), conversation_id: z.string().min(1) }),
        signal,
      })
    },
    list(options: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) {
      return http.request({
        method: 'GET',
        path: '/api/planner/plans',
        query: { limit: 20, ...options },
        schema: plannerListSchema,
        signal,
      })
    },
    detail(version: string, signal?: AbortSignal) {
      return http.request({
        method: 'GET',
        path: versionPath(version),
        schema: plannerDetailSchema,
        signal,
      })
    },
    history(plan: string, before?: number) {
      return http.request({
        method: 'GET',
        path: `/api/planner/plans/${part(plan)}/versions`,
        query: { before_version_no: before, limit: 20 },
        schema: historySchema,
      })
    },
    operation(operation: string) {
      return http.request({
        method: 'GET',
        path: `/api/planner/operations/${part(operation)}`,
        schema: plannerOperationSchema,
      })
    },
    restore(version: string, body: PlannerRestore) {
      return http.request({
        method: 'POST',
        path: `${versionPath(version)}/restore`,
        body: plannerRestoreSchema.parse(body),
        schema: plannerPublishedEventSchema,
      })
    },
    edit(version: string, body: PlannerEdit) {
      // No idempotency header: the HTTP client must never retry a local edit.
      return http.request({
        method: 'PATCH',
        path: versionPath(version),
        body: plannerEditSchema.parse(body),
        schema: plannerEditResultSchema,
      })
    },
    hotels(
      version: string,
      stay: string,
      revision: number,
      filter: PlannerHotelFilter = {},
      signal?: AbortSignal,
    ) {
      return http.request({
        method: 'GET',
        path: `${versionPath(version)}/stays/${part(stay)}/hotel-options`,
        query: { expected_revision: revision, page_size: 10, ...filter },
        timeoutMs: 30000,
        schema: plannerHotelCandidatesSchema,
        signal,
      })
    },
    hotel(
      version: string,
      stay: string,
      revision: number,
      hotel: string,
      provider: string,
      signal?: AbortSignal,
    ) {
      return http.request({
        method: 'GET',
        path: `${versionPath(version)}/stays/${part(stay)}/hotel-options/${part(hotel)}`,
        query: { expected_revision: revision, provider },
        timeoutMs: 30000,
        schema: plannerHotelPageDetailSchema,
        signal,
      })
    },
    rooms(
      version: string,
      stay: string,
      revision: number,
      hotel: string,
      provider: string,
      signal?: AbortSignal,
    ) {
      return http.request({
        method: 'GET',
        path: `${versionPath(version)}/stays/${part(stay)}/hotel-options/${part(hotel)}/rooms`,
        query: { expected_revision: revision, provider },
        timeoutMs: 30000,
        schema: plannerHotelRoomCandidatesSchema,
        signal,
      })
    },
    flights(
      version: string,
      transport: string,
      revision: number,
      cabin = 'unspecified',
      signal?: AbortSignal,
    ) {
      return http.request({
        method: 'GET',
        path: `${versionPath(version)}/transports/${part(transport)}/flight-options`,
        query: { expected_revision: revision, cabin_level: cabin },
        timeoutMs: 30000,
        schema: plannerFlightCandidatesSchema,
        signal,
      })
    },
    flight(
      version: string,
      transport: string,
      revision: number,
      candidate: string,
      signal?: AbortSignal,
    ) {
      return http.request({
        method: 'GET',
        path: `${versionPath(version)}/transports/${part(transport)}/flight-options/${part(candidate)}`,
        query: { expected_revision: revision },
        timeoutMs: 30000,
        schema: plannerFlightPageDetailSchema,
        signal,
      })
    },
    select(
      version: string,
      kind: 'hotel' | 'flight',
      module: string,
      body: PlannerSelectionRequest,
    ) {
      return http.request({
        method: 'PUT',
        path: `${versionPath(version)}/${kind === 'hotel' ? 'stays' : 'transports'}/${part(module)}/selection`,
        body: plannerSelectionRequestSchema.parse(body),
        schema: plannerEditResultSchema,
      })
    },
  }
}
export type PlannerApi = ReturnType<typeof createPlannerApi>
